import React, { useContext, useEffect } from 'react';
import { connect } from 'react-redux';

import { AppContext } from '@prisjakt/ui-app-context';

import Header from './header';
import Section from './section';

import { highlightDuration } from '../../../constants';
import { getProductListRootProps } from '../../../utils';
import { Product } from '../../../types';
import { AppState } from '../../../../../reducers';
import { setAddProductAnimation as setAddProductAnimationAction } from '../../../actions';

import * as S from './styles';

type ProductListRootProps = {
  id?: string | null;
  name?: string | null;
  children?: (string | null)[] | null;
};

type OwnProps = {
  highlightDifferences: boolean;
  products: Product[];
};

type StateProps = {
  shouldAnimate: boolean;
};

type DispatchProps = {
  setAddProductAnimation: (state: boolean) => void;
};

type Props = OwnProps & StateProps & DispatchProps;

const Columns = ({
  highlightDifferences,
  products,
  shouldAnimate,
  setAddProductAnimation,
}: Props) => {
  const { market } = useContext(AppContext);

  // Reset the `shouldAnimate` state when the animation is finished
  useEffect(() => {
    setTimeout(() => {
      setAddProductAnimation(false);
    }, highlightDuration);
  }, [shouldAnimate, setAddProductAnimation]);

  const sections = products
    ? getProductListRootProps(products).sort(
        (a: ProductListRootProps, b: ProductListRootProps) => {
          if (!a.name || !b.name) return 1;
          return a.name.localeCompare(b.name, market.locale.replace('_', '-'));
        }
      )
    : [];

  const renderHeaders = () => {
    return (
      <S.Headers>
        {products.map((product, index) => (
          <Header
            key={`column-header-${product.id}`}
            product={product}
            products={products}
            shouldAnimate={index + 1 === products.length && shouldAnimate}
          />
        ))}
        {products.length <= 1 && <Header isDummyHeader products={products} />}
      </S.Headers>
    );
  };

  const renderSections = () => (
    <>
      {sections.map(props => {
        const { id, name, children } = props;

        return (
          <Section
            key={`section-${id}`}
            sectionId={id}
            defaultOpen
            highlightDifferences={highlightDifferences}
            title={name}
            products={products}
            properties={children}
            shouldAnimate={shouldAnimate}
          />
        );
      })}
      {products.length < 1 && (
        <Section isDummySection sectionId="dummy-section" />
      )}
    </>
  );

  return (
    <S.Columns>
      {renderHeaders()}
      {renderSections()}
    </S.Columns>
  );
};

const mapStateToProps = (state: AppState) => ({
  shouldAnimate: state.areas.compare.shouldAnimate,
});

const dispatchActions = {
  setAddProductAnimation: setAddProductAnimationAction,
};

export default connect(mapStateToProps, dispatchActions)(Columns);
